import './MenuPersonalizar.scss';

import { Badge, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import HallAppToast from '../../commons/HallAppToast';
import { menuItems } from '../../template/Sidebar';

const LOCAL_STORAGE_KEY = 'hallapp-menu-config';

function flattenMenuItems(items) {
  const flat = [];

  items.forEach((item) => {
    const { children, ...rest } = item;

    const parentIsRoute = rest.isRoute !== false; // default: true

    if (rest.path && parentIsRoute) {
      flat.push({
        ...rest,
        isChild: false,
        parentLabel: null,
      });
    }

    if (children && children.length > 0) {
      children.forEach((child) => {
        const childIsRoute = child.isRoute !== false; // default: true
        if (!childIsRoute) return;

        flat.push({
          ...child,
          section: item.section || 'Outros',
          isChild: true,
          parentLabel: item.label,
        });
      });
    }
  });

  return flat;
}

function groupBySection(flatItems) {
  return flatItems.reduce((acc, item) => {
    const section = item.section || 'Outros';
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});
}

async function clearAppCacheAndReload() {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }

    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }

    window.location.reload();
  } catch (e) {
    console.error('Falha ao limpar cache do app:', e);
    window.location.reload();
  }
}

const MenuPersonalizar = () => {
  const flatMenu = useMemo(() => flattenMenuItems(menuItems), []);
  const groupedMenu = useMemo(() => groupBySection(flatMenu), [flatMenu]);
  const validPathsSet = useMemo(() => new Set(flatMenu.map((i) => i.path)), [flatMenu]);

  const [enabledMap, setEnabledMap] = useState({});
  const [defaultPath, setDefaultPath] = useState('inicio');

  const [isSavingHard, setIsSavingHard] = useState(false);
  const hasLoadedRef = useRef(false);
  const saveTimerRef = useRef(null);

  const [query, setQuery] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);

    const buildInitialEnabledAllTrue = () => {
      const initialEnabled = {};
      flatMenu.forEach((item) => {
        initialEnabled[item.path] = true;
      });

      flatMenu.forEach((item) => {
        if (item.label === 'Personalizar Menu') initialEnabled[item.path] = true;
      });

      return initialEnabled;
    };

    const pickSafeDefault = (candidateDefault, enabled) => {
      if (candidateDefault && validPathsSet.has(candidateDefault) && enabled[candidateDefault]) {
        return candidateDefault;
      }
      const firstEnabled = flatMenu.find((item) => enabled[item.path]);
      return firstEnabled?.path || 'inicio';
    };

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const savedEnabled = parsed.enabledMap || {};

        const nextEnabled = {};
        flatMenu.forEach((item) => {
          nextEnabled[item.path] = savedEnabled[item.path] ?? true;
        });

        flatMenu.forEach((item) => {
          if (item.label === 'Personalizar Menu') nextEnabled[item.path] = true;
        });

        const safeDefault = pickSafeDefault(parsed.defaultPath, nextEnabled);

        setEnabledMap(nextEnabled);
        setDefaultPath(safeDefault);
        hasLoadedRef.current = true;
        return;
      } catch (_) {
        console.warn('Erro ao ler configuração de menu do localStorage');
      }
    }

    const initialEnabled = buildInitialEnabledAllTrue();
    const safeDefault = initialEnabled['inicio']
      ? 'inicio'
      : (flatMenu.find((item) => initialEnabled[item.path])?.path || 'inicio');

    setEnabledMap(initialEnabled);
    setDefaultPath(safeDefault);
    hasLoadedRef.current = true;
  }, [flatMenu, validPathsSet]);

  useEffect(() => {
    if (!hasLoadedRef.current) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ enabledMap, defaultPath }));
      } catch (e) {
        console.error('Erro ao salvar configuração de menu', e);
        HallAppToast.genericError();
      }
    }, 250);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [enabledMap, defaultPath]);

  const handleToggleEnabled = (path) => {
    setEnabledMap((prev) => {
      const next = { ...prev, [path]: !prev[path] };

      if (!next[path] && defaultPath === path) {
        const firstEnabled = flatMenu.find((item) => next[item.path]);
        setDefaultPath(firstEnabled?.path || 'inicio');
      }

      return next;
    });
  };

  const handleSetDefault = (path) => {
    if (!validPathsSet.has(path)) return;

    setEnabledMap((prev) => ({ ...prev, [path]: true }));
    setDefaultPath(path);
  };

  const handleSaveAndClearCache = async () => {
    try {
      setIsSavingHard(true);

      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ enabledMap, defaultPath }));
      HallAppToast.success('Configurações salvas. Atualizando...');

      setTimeout(async () => {
        await clearAppCacheAndReload();
      }, 200);
    } catch (e) {
      console.error('Erro ao salvar e atualizar', e);
      HallAppToast.genericError('Não foi possível salvar/atualizar.');
      setIsSavingHard(false);
    }
  };

  const normalizedQuery = query.trim().toLowerCase();

  const filteredGroupedMenu = useMemo(() => {
    if (!normalizedQuery) return groupedMenu;

    const next = {};
    Object.entries(groupedMenu).forEach(([sectionTitle, items]) => {
      const filtered = items.filter((item) => {
        const label = (item.label || '').toLowerCase();
        const parent = (item.parentLabel || '').toLowerCase();
        return label.includes(normalizedQuery) || parent.includes(normalizedQuery);
      });
      if (filtered.length) next[sectionTitle] = filtered;
    });

    return next;
  }, [groupedMenu, normalizedQuery]);

  const stats = useMemo(() => {
    const total = flatMenu.length;
    const enabled = flatMenu.filter((i) => (enabledMap[i.path] ?? true)).length;
    const hidden = total - enabled;
    const defaultItem = flatMenu.find((i) => i.path === defaultPath);
    return { total, enabled, hidden, defaultLabel: defaultItem?.label || defaultPath };
  }, [flatMenu, enabledMap, defaultPath]);

  return (
    <Container fluid className="menu-personalizar">
      <div className="menu-personalizar__shell">
        <Row className="g-4">
          {/* LEFT */}
          <Col lg={8} xl={9}>
            <div className="menu-personalizar__header">
              <div>
                <h4 className="menu-personalizar__title">Personalizar Menu</h4>
                <p className="menu-personalizar__subtitle">
                  Escolha quais menus ficam visíveis e qual tela abre como padrão ao entrar no Hallapp.
                </p>
              </div>

              <div className="menu-personalizar__search">
                <Form.Control
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar menu (ex: Agenda, Caixa, Relatórios...)"
                />
              </div>
            </div>

            {Object.entries(filteredGroupedMenu).map(([sectionTitle, items]) => (
              <div key={sectionTitle} className="menu-personalizar__section">
                <div className="menu-personalizar__sectionHeader">
                  <div className="menu-personalizar__sectionTitle">{sectionTitle}</div>
                  <div className="menu-personalizar__sectionHint">
                    {items.length} item(ns)
                  </div>
                </div>

                <Row xs={1} sm={2} md={2} lg={3} xl={4} className="g-3">
                  {items.map((item) => {
                    const isEnabled = enabledMap[item.path] ?? true;
                    const isDefault = defaultPath === item.path;
                    const isPersonalizarMenu = item.label === 'Personalizar Menu';

                    return (
                      <Col key={item.path}>
                        <Card
                          className={[
                            'menu-personalizar__card',
                            !isEnabled && !isPersonalizarMenu ? 'is-disabled' : '',
                            isDefault ? 'is-default' : '',
                          ].join(' ')}
                        >
                          <Card.Body className="menu-personalizar__cardBody">
                            <div className="menu-personalizar__cardTop">
                              <div className="menu-personalizar__cardTitleWrap">
                                <div className="menu-personalizar__cardTitle">
                                  {item.label}
                                </div>

                                {item.isChild && item.parentLabel && (
                                  <div className="menu-personalizar__cardSub">
                                    Submenu de {item.parentLabel}
                                  </div>
                                )}
                              </div>

                              <div className="menu-personalizar__badges">
                                {isDefault && <Badge bg="primary">Inicial</Badge>}
                                {!isEnabled && !isPersonalizarMenu && <Badge bg="secondary">Oculto</Badge>}
                              </div>
                            </div>

                            <div className="menu-personalizar__controls">
                              <Form.Check
                                type="switch"
                                id={`toggle-${item.path}`}
                                label="Mostrar no menu"
                                checked={isPersonalizarMenu ? true : isEnabled}
                                disabled={isPersonalizarMenu}
                                onChange={isPersonalizarMenu ? undefined : () => handleToggleEnabled(item.path)}
                              />

                              <Form.Check
                                type="radio"
                                name="default-menu"
                                id={`default-${item.path}`}
                                label="Definir como menu inicial"
                                checked={isDefault}
                                onChange={() => handleSetDefault(item.path)}
                                disabled={!isEnabled && !isPersonalizarMenu}
                              />
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            ))}

            <div className="menu-personalizar__spacerBottom" />
          </Col>

          {/* RIGHT (sticky summary) */}
          <Col lg={4} xl={3} className="d-none d-lg-block">
            <div className="menu-personalizar__sticky">
              <Card className="menu-personalizar__summary">
                <Card.Body>
                  <div className="menu-personalizar__summaryTitle">Resumo</div>

                  <div className="menu-personalizar__summaryRow">
                    <span>Total</span>
                    <strong>{stats.total}</strong>
                  </div>
                  <div className="menu-personalizar__summaryRow">
                    <span>Visíveis</span>
                    <strong>{stats.enabled}</strong>
                  </div>
                  <div className="menu-personalizar__summaryRow">
                    <span>Ocultos</span>
                    <strong>{stats.hidden}</strong>
                  </div>

                  <div className="menu-personalizar__summaryDivider" />

                  <div className="menu-personalizar__summaryRow">
                    <span>Menu inicial</span>
                    <strong title={stats.defaultLabel}>{stats.defaultLabel}</strong>
                  </div>

                  <Button
                    variant="primary"
                    className="w-100 mt-3"
                    onClick={handleSaveAndClearCache}
                    disabled={isSavingHard}
                  >
                    {isSavingHard ? 'Salvando e atualizando...' : 'Salvar alterações'}
                  </Button>

                  <div className="menu-personalizar__summaryHint">
                    Ao salvar, o app atualiza para aplicar o menu imediatamente.
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>

        {/* Bottom fixed bar (mobile) */}
        <div className="menu-personalizar__bottom-bar d-lg-none">
          <Button
            variant="primary"
            className="w-100"
            onClick={handleSaveAndClearCache}
            disabled={isSavingHard}
          >
            {isSavingHard ? 'Salvando e atualizando...' : 'Salvar alterações'}
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default MenuPersonalizar;
